import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextType {
  success: (msg: string, duration?: number) => void;
  error: (msg: string, duration?: number) => void;
  info: (msg: string, duration?: number) => void;
  warning: (msg: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let toastId = 0;

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((toasts) => toasts.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: ToastType, message: string, duration = 3000) => {
      const id = ++toastId;
      const newToast: Toast = { id, type, message };
      setToasts((prev) => [...prev, newToast]);

      setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  const value: ToastContextType = {
    success: (msg, duration) => addToast("success", msg, duration),
    error: (msg, duration) => addToast("error", msg, duration),
    info: (msg, duration) => addToast("info", msg, duration),
    warning: (msg, duration) => addToast("warning", msg, duration),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-3">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// --- ToastItem Component ---
interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose }) => {
  const { type, message } = toast;

  const typeStyles: Record<ToastType, string> = {
    success: "bg-green-500",
    error: "bg-red-500",
    info: "bg-blue-500",
    warning: "bg-yellow-400 text-black",
  };

  return (
    <div
      className={`flex items-center justify-between min-w-[250px] max-w-sm px-4 py-2 rounded-xl shadow-lg text-white animate-slideIn ${typeStyles[type]}`}
    >
      <span className="font-medium">{message}</span>
      <button
        onClick={onClose}
        className="ml-3 text-white/80 hover:text-white transition"
        aria-label="Close toast"
      >
        ✕
      </button>
    </div>
  );
};